import { FC } from "react";
import { FaTrash } from "react-icons/fa";
import { FcLock } from "react-icons/fc";
import { Table } from "reactstrap";

import {
  BookedAmenitiesTableProps,
  CheckAvailabilityButtonProps,
} from "@models/components/amenity.model";
import { formatPrice } from "@utils/common.utils";

const CheckAvailabilityButton: FC<CheckAvailabilityButtonProps> = ({
  id,
  isMobile = false,
  onCheckNextAvailability,
  t,
  title,
}) => {
  if (!onCheckNextAvailability) return null;

  return (
    <button
      id={`fg-btn-check-availability-${isMobile ? "mobile" : "table"}-${id}`}
      type="button"
      className={`btn btn-outline-primary btn-sm rounded-pill px-2 py-0 fg-btn-check-availability ${
        isMobile ? "small" : ""
      }`.trim()}
      onClick={e =>
        onCheckNextAvailability(
          id,
          title,
          e.currentTarget.id || e.currentTarget
        )
      }
    >
      {t("checkNextAvailability")}
    </button>
  );
};

export const BookedAmenitiesTable: FC<BookedAmenitiesTableProps> = ({
  bookedDataSelections,
  getBookedCardProps,
  t,
}) => {
  return (
    <>
      {/* Desktop View: Table */}
      <div className="table-responsive border rounded-3 shadow-sm bg-white mb-4 d-none d-md-block">
        <Table hover className="align-middle mb-0">
          <thead className="table-light text-uppercase">
            <tr className="text-muted small fw-bold">
              <th className="px-3 py-3 col-6">{t("amenity")}</th>
              <th className="text-center px-3 py-3 col-2">{t("price")}</th>
              <th className="text-center px-3 py-3 col-1">{t("quantity")}</th>
              <th className="text-end px-3 py-3 col-2">{t("total")}</th>
              <th className="text-center px-3 py-3 col-1"></th>
            </tr>
          </thead>
          <tbody>
            {bookedDataSelections.map(selectedAmenity => {
              const cardProps = getBookedCardProps(selectedAmenity);
              const totalAmount = cardProps.quantity * cardProps.price;
              const formattedPrice = formatPrice(
                cardProps.price,
                cardProps.currency
              );
              const formattedTotal = formatPrice(
                totalAmount,
                cardProps.currency
              );

              return (
                <tr
                  key={cardProps.key}
                  className={cardProps.isDisabled ? "table-light" : ""}
                >
                  <td
                    className={`px-3 py-3 ${cardProps.isDisabled ? "opacity-50" : ""}`}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold text-dark">
                        {cardProps.title}
                      </span>
                      {cardProps.isMandatory && (
                        <FcLock
                          className="fs-5"
                          data-tooltip-id="amenity-mandatory-tooltip"
                          data-tooltip-content={t("mandatory")}
                        />
                      )}
                      {cardProps.error && (
                        <>
                          <span className="badge bg-danger">
                            {cardProps.error}
                          </span>
                          <CheckAvailabilityButton
                            id={cardProps.id}
                            title={cardProps.title}
                            onCheckNextAvailability={
                              cardProps.onCheckNextAvailability
                            }
                            t={t}
                          />
                        </>
                      )}
                    </div>
                  </td>
                  <td
                    className={`text-center text-muted px-3 py-3 ${cardProps.isDisabled ? "opacity-50" : ""}`}
                  >
                    {formattedPrice}
                  </td>
                  <td
                    className={`text-center px-3 py-3 ${cardProps.isDisabled ? "opacity-50" : ""}`}
                  >
                    <span className="badge bg-light text-dark px-3 py-2 border font-monospace fs-6">
                      {cardProps.quantity}
                    </span>
                  </td>
                  <td
                    className={`text-end fw-bold text-primary px-3 py-3 ${cardProps.isDisabled ? "opacity-50" : ""}`}
                  >
                    {formattedTotal}
                  </td>
                  <td className="text-center px-3 py-3">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 fg-btn-delete-circle"
                      onClick={() =>
                        cardProps.onRemove(cardProps.id, true, cardProps.uuid)
                      }
                      title={t("remove")}
                    >
                      <FaTrash size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Mobile View: Stacked list of Cards */}
      <div className="fg-booked-amenities-mobile d-block d-md-none mb-4">
        {bookedDataSelections.map(selectedAmenity => {
          const cardProps = getBookedCardProps(selectedAmenity);
          const totalAmount = cardProps.quantity * cardProps.price;
          const formattedPrice = formatPrice(
            cardProps.price,
            cardProps.currency
          );
          const formattedTotal = formatPrice(totalAmount, cardProps.currency);

          return (
            <div
              key={cardProps.key}
              className={`fg-booked-amenity-card p-3 border rounded-3 mb-2 position-relative ${
                cardProps.error
                  ? "unavailable"
                  : cardProps.isDisabled
                    ? "opacity-75 bg-light"
                    : "bg-white"
              }`}
            >
              <div className="d-flex justify-content-between align-items-start mb-2 pe-4">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-bold text-dark fs-6">
                    {cardProps.title}
                  </span>
                  {cardProps.isMandatory && (
                    <FcLock
                      className="fs-5"
                      data-tooltip-id="amenity-mandatory-tooltip"
                      data-tooltip-content={t("mandatory")}
                    />
                  )}
                  {cardProps.error && (
                    <>
                      <span className="badge bg-danger text-wrap small">
                        {cardProps.error}
                      </span>
                      <CheckAvailabilityButton
                        id={cardProps.id}
                        isMobile
                        title={cardProps.title}
                        onCheckNextAvailability={
                          cardProps.onCheckNextAvailability
                        }
                        t={t}
                      />
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 fg-btn-delete-circle position-absolute"
                  onClick={() =>
                    cardProps.onRemove(cardProps.id, true, cardProps.uuid)
                  }
                  title={t("remove")}
                >
                  <FaTrash size={11} />
                </button>
              </div>

              <div className="d-flex justify-content-between align-items-center fg-card-details">
                <div className="text-secondary small">
                  <div>
                    {t("price")}:{" "}
                    <span className="fw-semibold text-dark">
                      {formattedPrice}
                    </span>
                  </div>
                  <div className="mt-1">
                    {t("quantity")}:{" "}
                    <span className="badge bg-white text-dark border px-2 py-1 font-monospace ms-1">
                      {cardProps.quantity}
                    </span>
                  </div>
                </div>
                <div className="text-end">
                  <div className="small text-muted">{t("total")}</div>
                  <div className="fw-bold text-primary fs-6">
                    {formattedTotal}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
